/**
 * Recommendations Test Page
 *
 * Fetches personalised recommendations from POST /recommendations
 * and renders them as a product grid using Handlebars.
 *
 * The "Rebuild" button calls POST /recommendations/rebuild/:userId
 * and then re-fetches recommendations.
 */
(function () {
  'use strict';

  // --- helpers ---------------------------------------------------------------

  /** API base without the /shopify suffix */
  function getApiBase() {
    return window.API_BASE_URL
      ? window.API_BASE_URL.replace(/\/shopify\/?$/, '')
      : '';
  }

  /** Resolve the Firebase userId from session storage or auth state */
  async function getUserId() {
    try {
      var id = sessionStorage.getItem('firebaseUserId');
      if (id) return id;
      if (window.firebaseAuth) {
        var user = await window.firebaseAuth.waitForAuthState(3000);
        return (user && user.uid) || null;
      }
      return null;
    } catch {
      return null;
    }
  }

  // --- Handlebars setup ------------------------------------------------------

  // Reuse the shared Handlebars promise if available (from cart-product-recommendations),
  // otherwise create our own polling promise.
  if (!window.__handlebarsPromise) {
    window.__handlebarsPromise = new Promise(function (resolve, reject) {
      if (typeof Handlebars !== 'undefined') {
        resolve(Handlebars);
        return;
      }

      var rafAttempts = 0;
      var maxRafAttempts = 10;

      var checkWithRAF = function () {
        if (typeof Handlebars !== 'undefined') {
          resolve(Handlebars);
          return;
        }
        rafAttempts++;
        if (rafAttempts < maxRafAttempts) {
          requestAnimationFrame(checkWithRAF);
        } else {
          var attempts = 0;
          var maxAttempts = 48;
          var checkInterval = setInterval(function () {
            attempts++;
            if (typeof Handlebars !== 'undefined') {
              clearInterval(checkInterval);
              resolve(Handlebars);
            } else if (attempts >= maxAttempts) {
              clearInterval(checkInterval);
              reject(new Error('Handlebars failed to load after 5 seconds'));
            }
          }, 100);
        }
      };

      requestAnimationFrame(checkWithRAF);
    });
  }

  function waitForHandlebars() {
    return window.__handlebarsPromise;
  }

  // --- DOM refs --------------------------------------------------------------

  var listEl = document.getElementById('recommendations-list');
  var spinnerEl = document.getElementById('recommendationsSpinner');
  var statusEl = document.getElementById('recommendationsStatus');

  // --- source labels ---------------------------------------------------------

  var SOURCE_LABELS = {
    profile: 'Based on your activity',
    popularity: 'Popular item',
    similar_users: 'Users like you also liked',
  };

  function addSourceLabels(products) {
    if (!products) return products;
    return products.map(function (p) {
      p.sourceLabel = SOURCE_LABELS[p.source] || p.source || '';
      return p;
    });
  }

  // --- rendering -------------------------------------------------------------

  var compiledTemplate = null;

  function showSpinner() {
    spinnerEl.style.display = 'flex';
    listEl.innerHTML = '';
  }

  function hideSpinner() {
    spinnerEl.style.display = 'none';
  }

  function setStatus(msg) {
    statusEl.textContent = msg;
  }

  async function renderProducts(products) {
    var Handlebars = await waitForHandlebars();

    if (!compiledTemplate) {
      var templateSrc = document.getElementById('recommendations-product-template').innerHTML;
      compiledTemplate = Handlebars.compile(templateSrc);

      // Register helper if not already registered
      if (!Handlebars.helpers.compare) {
        Handlebars.registerHelper('compare', function (a, comparator, b) {

          return eval(a + comparator + b) ? true : false;
        });
      }
    }

    if (!products || products.length === 0) {
      listEl.innerHTML = '<li class="grid__item" style="grid-column: 1 / -1; text-align: center; padding: 2rem;">No recommendations found.</li>';
      return;
    }

    var enriched = addSourceLabels(products);
    listEl.innerHTML = compiledTemplate({ products: enriched });
  }

  // --- authenticated fetch with auto-refresh ---------------------------------

  /**
   * Refresh the Firebase ID token via the SDK, update sessionStorage,
   * and return the new token.
   */
  async function refreshIdToken() {
    var auth = window.firebaseAuth;
    if (!auth) throw new Error('Firebase auth not available');

    var user = await auth.waitForAuthState(3000);
    if (!user) throw new Error('No Firebase user — please log in again');

    var newToken = await user.getIdToken(true);
    sessionStorage.setItem('firebaseIdToken', newToken);
    return newToken;
  }

  /**
   * Wrapper around fetch that automatically adds the Authorization header
   * and retries once with a refreshed token on 401 "Expired or invalid id token".
   *
   * @param {string} url
   * @param {object} [options]  – standard fetch options (method, body, etc.)
   * @returns {Promise<Response>}
   */
  async function apiFetch(url, options) {
    options = options || {};
    var token = sessionStorage.getItem('firebaseIdToken');

    var headers = Object.assign(
      { 'Content-Type': 'application/json' },
      options.headers || {},
      token ? { 'Authorization': 'Bearer ' + token } : {}
    );

    var response = await fetch(url, Object.assign({}, options, { headers: headers }));

    if (response.status === 401) {
      try {
        var body = await response.clone().json();
        if (body && body.message && body.message.indexOf('Expired or invalid id token') !== -1) {
          var freshToken = await refreshIdToken();
          headers['Authorization'] = 'Bearer ' + freshToken;
          response = await fetch(url, Object.assign({}, options, { headers: headers }));
        }
      } catch (e) {
        console.error(`[recommendations-test] error calling ${url}`, e);
        // couldn't parse body or refresh failed — fall through with original response
      }
    }

    return response;
  }

  // --- API calls -------------------------------------------------------------

  /** Read ?userId= from the page URL if present */
  function getQueryParamUserId() {
    var params = new URLSearchParams(window.location.search);
    return params.get('userId') || null;
  }

  async function fetchRecommendations() {
    var base = getApiBase();
    if (!base) throw new Error('API_BASE_URL not set');

    var url = base + '/recommendations';
    var overrideUserId = getQueryParamUserId();
    if (overrideUserId) {
      url += '?userId=' + encodeURIComponent(overrideUserId);
    }

    var response = await apiFetch(url, { method: 'GET' });

    if (!response.ok) {
      throw new Error('Failed to fetch recommendations: ' + response.status);
    }

    return response.json();
  }

  // --- main flow -------------------------------------------------------------

  async function loadRecommendations() {
    showSpinner();
    setStatus('');

    try {
      var userId = await getUserId();

      var data = await fetchRecommendations(userId);
      hideSpinner();
      await renderProducts(data.recommendations.products || data);
      const recommendsForUserId = getQueryParamUserId() || userId;
      setStatus(
        'Showing ' + ((data.recommendations.products || data).length || 0) + ' recommendations' +
        (recommendsForUserId ? ' for user ' + recommendsForUserId : '') + '.'
      );
    } catch (err) {
      hideSpinner();
      setStatus('Error loading recommendations: ' + err.message);
      console.error('[recommendations-test]', err);
    }
  }

  // --- kick off --------------------------------------------------------------

  loadRecommendations();
})();
